import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CancelIcon from '@mui/icons-material/Cancel';
import Divider from "@mui/material/Divider";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Table from '../helpers/Table'
import DeletePopup from "../helpers/DeletePopup";

import { addDriver, editDriver, deleteDriver } from "../../actions/valueActions";

import { convertBoolDiffValue } from "./valueFunctions";
import firstCharToUpper from "../helpers/stringFormat";
import CostValueDriverInventoryForm from "./CostValueDriverInventoryForm";
import { getIconFromDriverName } from "./iconFunctions";
import { getAllOutcomes } from "./valueFunctions";

const CostValueDriverInventory = ({ id, valueModel, productionCosts, warehouseCosts, driver, cost, outcome }) => {
    const formikRef = useRef(null);
    const dispatch = useDispatch();

    const [deleteInventoryDriverPopupOpen, setDeleteInventoryDriverPopupOpen] = useState(false);
    const [deleteInventoryDriverId, setDeleteInventoryDriverId] = useState(null);
    const [editInventoryDriverId, setEditInventoryDriverId] = useState(null);

    const openDeleteInventoryDriver = (id) => {
        setDeleteInventoryDriverId(id)
        setDeleteInventoryDriverPopupOpen(true)
    }

    const editInventoryDriver = (id) => {
        setEditInventoryDriverId(id)
    }
    
    const onSubmitInventory = async (values, driver, cost, { setSubmitting, setErrors }) => {
        values.id = editInventoryDriverId
        if (outcome) {
            values.outcome = outcome
        }
        values.cost = driver
        values.sub_cost = cost
        values.multiple_value = values.multiple_value.toString().replace(/,/g, "");
        let success = false

        if (editInventoryDriverId) {
            const result = await dispatch(editDriver('inventory', id, values, { setSubmitting, setErrors }))
            success = result?.success;
            if (success) {
                setEditInventoryDriverId(null)
            }
        }
        else {
            const result = await dispatch(addDriver('inventory', id, values, { setSubmitting, setErrors }))
            success = result?.success;
        }

        if (formikRef.current && success) {
            formikRef.current.resetForm();
        }
    }
    
    const handleResetInventory = () => {
        setEditInventoryDriverId(null)

        // Access the formikRef current value and call resetForm
        if (formikRef.current) {
            formikRef.current.resetForm();
        }
    };
    
    const inventoryDriverColumns = [
        { id: 'outcome', label: 'Outcome', minWidth: 170, action: false },
        { id: 'driver_name', label: 'Name', minWidth: 170, action: false },
        { id: 'multiple_type', label: 'Type of Storage', format: (value) => firstCharToUpper(value), minWidth: 150, action: false },
        { id: 'alternative_returns', label: 'Alternative returns', format: (value) => value + '%', minWidth: 100, action: false },
        { id: 'efficiency_gain', label: 'Stock', minWidth: 100, action: false },
        { id: 'positive_driver', label: 'Differentiation value', minWidth: 100, format: (value) => convertBoolDiffValue(Boolean(value)), action: false },
        { id: 'actions', label: 'Actions', minWidth: 100, action: false },
    ];

    const createInventoryDriverData = (id, outcome, driver_name, sub_cost, multiple_type, efficiency_gain, alternative_returns, positive_driver, actions) => {
        efficiency_gain = `${efficiency_gain}% ${positive_driver ? 'reduction': 'increase'}`
        return { id, outcome, driver_name, sub_cost, multiple_type, efficiency_gain, alternative_returns, positive_driver, actions };
    }
    
    const inventoryDriverActions = (id) => {
        return (
            <React.Fragment>
                <IconButton size="small" onClick={() => editInventoryDriver(id)}>
                    <EditIcon />
                </IconButton>
                <IconButton size="small" onClick={() => openDeleteInventoryDriver(id)}>
                    <DeleteIcon />
                </IconButton>
            </React.Fragment>
        )
    }
    
    const inventoryDriverRows = valueModel?.cost_value_driver.inventory_savings.filter(driver => !outcome || driver.outcome === outcome).map((driver) => {
        return (
            createInventoryDriverData(driver.id, driver.outcome, driver.driver_name, driver.sub_cost, driver.multiple_type, driver.efficiency_gain, driver.alternative_returns, driver.positive_driver, inventoryDriverActions(driver.id))
        )
    })
    
    const inventoryDriverTable = () => {
        return (
            <React.Fragment>
                <Divider />
                <Table
                    columns={inventoryDriverColumns}
                    rows={inventoryDriverRows}
                    selectedId={editInventoryDriverId}
                />
            </React.Fragment>
        )
    }
    
    const inventoryDriverFormActions = () => {
        return (
            <React.Fragment>
                {editInventoryDriverId ? null
                    : <Button variant="contained" startIcon={<Box component={getIconFromDriverName(driver)} />} size="small" type="submit">Add driver</Button>
                }
                {editInventoryDriverId ?
                    <Stack direction="row" spacing={2}>
                        <Button variant="contained" startIcon={<EditIcon />} size="small" type="submit">Save</Button>
                        <Button variant="contained" startIcon={<CancelIcon />} size="small" onClick={handleResetInventory}>Cancel</Button>
                    </Stack>
                    : null
                }
            </React.Fragment>
        )
    }
    
    return (
        <Box>
            <Divider sx={{ mb: 3 }} />
            <Stack gap={1} direction="row" sx={{ ml: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    Driver type:
                </Typography>
                <Typography variant="caption">
                    Operating Capital Savings
                </Typography>
            </Stack>

            <CostValueDriverInventoryForm
                currency="€"
                outcome={outcome}
                outcomeList={getAllOutcomes(valueModel?.driver_analysis)}
                costStructure={warehouseCosts}
                productionCosts={productionCosts}
                editInventoryDriverId={editInventoryDriverId}
                editInventoryDriver={valueModel?.cost_value_driver.inventory_savings.find(inventoryDriver => inventoryDriver.id === editInventoryDriverId)}
                formActions={inventoryDriverFormActions}
                onSubmit={(values, { setSubmitting, setErrors }) => onSubmitInventory(values, driver, 'inventory', { setSubmitting, setErrors })}
                ref={formikRef}
            />
            {inventoryDriverTable()}
            <DeletePopup open={deleteInventoryDriverPopupOpen} onClose={() => setDeleteInventoryDriverPopupOpen(false)} onDelete={() => dispatch(deleteDriver('inventory', id, deleteInventoryDriverId))} />
        </Box>
    )
}

export default CostValueDriverInventory